import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { v4 as uuidv4 } from 'uuid';
import { computed, inject } from '@angular/core';
import { FuelSpecsStore } from '@navstation-portal/fuel-specs/data-access/store';
import { ReportStore } from '@navstation-portal/reporting/data-access/vessel-report';
import { AnyOffshoreSectionState, OffshoreBunkerState } from '@navstation-portal/reporting/data-model/offshore-report';
import {
  BunkerRowEntry,
  BunkerRowFormState,
  INIT_MODES,
  InitMode,
  StockTank,
  StockTankFormState,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { signalStore, withComputed, withState } from '@ngrx/signals';
import { initStateField } from '../../../offshore-report-utils/store.utility';
import { createDynamicSectionState } from '../../offshore-vm-builder-helpers';
import { withOffshoreFormStoreReducer } from '../../with-offshore-form-store-reducer';
import { buildOffshoreBunkerTableVm, buildOffshoreBunkerVm } from './offshore-bunker-vm-builder';
import { withOffshoreBunkerEffects } from './with-offshore-bunker-effects';
import { offshoreBunkerFormEvents } from './with-offshore-bunker-events';
import { withOffshoreBunkerReducer } from './with-offshore-bunker-reducer';

const generateBunkerRowArray = (existingRows: BunkerRowEntry[]): BunkerRowFormState[] => {
  if (!existingRows?.length) {
    return [generateBunkerRow()];
  }

  return existingRows.map((row) => generateBunkerRow(row));
};

const generateBunkerRow = (bunkerRow?: BunkerRowEntry): BunkerRowFormState => ({
  fuelType: initStateField<SelectOption>(
    bunkerRow?.fuelType
      ? {
          name: bunkerRow?.fuelType.toUpperCase(),
          code: bunkerRow?.fuelType,
        }
      : undefined
  ),
  isoFuelGrade: initStateField<SelectOption>(
    bunkerRow?.isoFuelGrade
      ? {
          name: bunkerRow?.isoFuelGrade,
          code: bunkerRow?.isoFuelGrade,
        }
      : undefined
  ),
  quantityOrdered: initStateField<number>(bunkerRow?.quantityOrdered),
  quantityReceived: initStateField<number>(bunkerRow?.quantityReceived),
  bdnFigures: initStateField<string>(bunkerRow?.bdnFigures),
  density: initStateField<number>(bunkerRow?.density),
  lcv: initStateField<number>(bunkerRow?.lcv),
  sulphur: initStateField<number>(bunkerRow?.sulphur),
  viscosity: initStateField<number>(bunkerRow?.viscosity),
  labSulphur: initStateField<number>(bunkerRow?.labSulphur),
  rowId: initStateField<string>(uuidv4()),
  stableUntil: initStateField<Date | undefined>(),
});

const generateTankStockArray = (stockTanks: Record<string, StockTank | undefined>) => {
  const tanks: StockTankFormState[] = [];

  Object.entries(stockTanks).forEach(([key, tankValue]) => {
    tanks.push({
      id: initStateField<string>(key),
      _id: initStateField<string | undefined>(tankValue?._id?.$oid),
      amount: initStateField<number | undefined>(tankValue?.amount),
      amountBeforeBunkering: initStateField<number | undefined>(tankValue?.amountBeforeBunkering),
      bdnNumber: initStateField<string | undefined>(tankValue?.bdnNumber),
      bunkerDate: initStateField<Date | undefined>(tankValue?.bunkerDate ? new Date(tankValue.bunkerDate) : undefined),
      bunkerPort: initStateField<string | undefined>(tankValue?.bunkerPort),
      density: initStateField<number | undefined>(tankValue?.density),
      densityBeforeBunkering: initStateField<number | undefined>(tankValue?.densityBeforeBunkering),
      fuelClass: initStateField<string | undefined>(tankValue?.fuelClass),
      fuelGrade: initStateField<SelectOption | undefined>(tankValue?.fuelGrade),
      fuelGradeAddedInTank: initStateField<SelectOption | undefined>(tankValue?.fuelGradeAddedInTank),
      groupColor: initStateField<string | undefined>(undefined),
      inUse: initStateField<boolean | undefined>(tankValue?.inUse),
      isBlend: initStateField<boolean | undefined>(tankValue?.isBlend),
      index: initStateField<number | undefined>(undefined),
      lcv: initStateField<number | undefined>(tankValue?.lcv),
      lcvBeforeBunkering: initStateField<number | undefined>(tankValue?.lcvBeforeBunkering),
      stableUntil: initStateField<Date | undefined>(
        tankValue?.stableUntil ? new Date(tankValue.stableUntil) : undefined
      ),
      sulphurBeforeBunkering: initStateField<number | undefined>(tankValue?.sulphurBeforeBunkering),
      sulphurAfterBunkering: initStateField<number | undefined>(tankValue?.sulphurAfterBunkering),
      quantityAddedInTank: initStateField<number | undefined>(tankValue?.quantityAddedInTank),
      tankCapacity: initStateField<number | undefined>(undefined),
      tankName: initStateField<string | undefined>(undefined),
      viscosity: initStateField<number | undefined>(tankValue?.viscosity),
    });
  });

  return tanks;
};

const initOffshoreBunkerStateReducer = (initialData: AnyOffshoreSectionState, initMode: InitMode) => {
  const data = initialData as OffshoreBunkerState;
  switch (initMode) {
    case INIT_MODES.CARRY_OVER:
      return initWithCarryOver(data);
    case INIT_MODES.IMPORT:
      return initWithExistingReport(data);
    case INIT_MODES.BLANK:
      return initBlank();
    default:
      return initBlank();
  }
};

const initBlank = () => buildBunkerState();
const initWithCarryOver = (data: OffshoreBunkerState) => buildBunkerState(data);
const initWithExistingReport = (data: OffshoreBunkerState) => buildBunkerState(data);

function buildBunkerState(data?: OffshoreBunkerState) {
  return {
    options: {
      tookFuelOil: initStateField<boolean>(data?.options?.tookFuelOil),
      tookLubeOil: initStateField<boolean>(data?.options?.tookLubeOil),
      debunkered: initStateField<boolean>(data?.options?.debunkered),
    },
    fuelOilBunkers: {
      startedBunkering: initStateField<Date>(
        data?.fuelOilBunkers?.startedBunkering ? new Date(data.fuelOilBunkers.startedBunkering) : undefined
      ),
      finishedBunkering: initStateField<Date>(
        data?.fuelOilBunkers?.finishedBunkering ? new Date(data.fuelOilBunkers.finishedBunkering) : undefined
      ),
      bargeAlongside: initStateField<Date>(
        data?.fuelOilBunkers?.bargeAlongside ? new Date(data.fuelOilBunkers.bargeAlongside) : undefined
      ),
      bargeAway: initStateField<Date>(
        data?.fuelOilBunkers?.bargeAway ? new Date(data.fuelOilBunkers.bargeAway) : undefined
      ),
    },
    fuelOilBunkerTable: {
      bunkerRows: generateBunkerRowArray(data?.fuelOilBunkerTable?.bunkerRows ?? []),
      quantityOrderedFreshWater: initStateField<number>(data?.fuelOilBunkerTable?.quantityOrderedFreshWater),
      quantityReceivedFreshWater: initStateField<number>(data?.fuelOilBunkerTable?.quantityReceivedFreshWater),
      bdnFiguresFreshWater: initStateField<string>(data?.fuelOilBunkerTable?.bdnFiguresFreshWater),
      quantityOrderedDistilledWater: initStateField<number>(data?.fuelOilBunkerTable?.quantityOrderedDistilledWater),
      quantityReceivedDistilledWater: initStateField<number>(data?.fuelOilBunkerTable?.quantityReceivedDistilledWater),
      bdnFiguresDistilledWater: initStateField<string>(data?.fuelOilBunkerTable?.bdnFiguresDistilledWater),
      protestIssued: initStateField<boolean>(data?.fuelOilBunkerTable?.protestIssued),
    },
    tankStockTable: {
      tanks: generateTankStockArray(data?.tankStockTable?.tanks || {}),
    },
    debunkered: {
      startedDebunkering: initStateField<Date>(
        data?.debunkered?.startedDebunkering ? new Date(data.debunkered.startedDebunkering) : undefined
      ),
      finishedDebunkering: initStateField<Date>(
        data?.debunkered?.finishedDebunkering ? new Date(data.debunkered.finishedDebunkering) : undefined
      ),
      debunkeredFuelGrade0: initStateField<SelectOption>(data?.debunkered?.debunkeredFuelGrade0),
      debunkeredQuantity0: initStateField<number>(data?.debunkered?.debunkeredQuantity0),
      debunkeredFuelGrade1: initStateField<SelectOption>(data?.debunkered?.debunkeredFuelGrade1),
      debunkeredQuantity1: initStateField<number>(data?.debunkered?.debunkeredQuantity1),
    },
    lubeOilBunkers: {
      startedBunkering: initStateField<Date>(
        data?.lubeOilBunkers?.startedBunkering ? new Date(data.lubeOilBunkers.startedBunkering) : undefined
      ),
      finishedBunkering: initStateField<Date>(
        data?.lubeOilBunkers?.finishedBunkering ? new Date(data.lubeOilBunkers.finishedBunkering) : undefined
      ),
      meLubeOilSystemQuantity: initStateField<number>(data?.lubeOilBunkers?.meLubeOilSystemQuantity),
      meLubeOilSystemFigures: initStateField<string>(data?.lubeOilBunkers?.meLubeOilSystemFigures),
      meLubeOilSpareQuantity: initStateField<number>(data?.lubeOilBunkers?.meLubeOilSpareQuantity),
      meLubeOilSpareFigures: initStateField<string>(data?.lubeOilBunkers?.meLubeOilSpareFigures),
      lowBnCylinderOilQuantity: initStateField<number>(data?.lubeOilBunkers?.lowBnCylinderOilQuantity),
      lowBnCylinderOilFigures: initStateField<string>(data?.lubeOilBunkers?.lowBnCylinderOilFigures),
      highBnCylinderOilQuantity: initStateField<number>(data?.lubeOilBunkers?.highBnCylinderOilQuantity),
      highBnCylinderOilFigures: initStateField<string>(data?.lubeOilBunkers?.highBnCylinderOilFigures),
      auxiliaryEngineLubeOilQuantity: initStateField<number>(data?.lubeOilBunkers?.auxiliaryEngineLubeOilQuantity),
      auxiliaryEngineLubeOilFigures: initStateField<string>(data?.lubeOilBunkers?.auxiliaryEngineLubeOilFigures),
      turboOilQuantity: initStateField<number>(data?.lubeOilBunkers?.turboOilQuantity),
      turboOilFigures: initStateField<string>(data?.lubeOilBunkers?.turboOilFigures),
      hydraulicOilQuantity: initStateField<number>(data?.lubeOilBunkers?.hydraulicOilQuantity),
      hydraulicOilFigures: initStateField<string>(data?.lubeOilBunkers?.hydraulicOilFigures),
      otherOilQuantity: initStateField<number>(data?.lubeOilBunkers?.otherOilQuantity),
      otherOilFigures: initStateField<string>(data?.lubeOilBunkers?.otherOilFigures),
      protestIssued: initStateField<boolean>(data?.lubeOilBunkers?.protestIssued),
    },
    dynamicSection: createDynamicSectionState([]),
  };
}

export type BunkerBuilderState = ReturnType<typeof buildBunkerState>;
export const OffshoreBunkerFormStore = signalStore(
  { providedIn: 'root' },
  withDevtools('OffshoreBunkerFormStore'),
  withState(initBlank()),
  withOffshoreBunkerEffects(),
  withOffshoreBunkerReducer(),
  withOffshoreFormStoreReducer(offshoreBunkerFormEvents, initOffshoreBunkerStateReducer, initBlank),
  withComputed((store) => {
    const reportStore = inject(ReportStore);
    const fuelSpecsStore = inject(FuelSpecsStore);

    return {
      vm: computed(() => {
        return buildOffshoreBunkerVm(store);
      }),
      bunkerTableVm: computed(() => {
        const fuelSpecsVm = fuelSpecsStore.fuelspecsVm();
        return buildOffshoreBunkerTableVm(
          reportStore.availableFuelGradeOptions(),
          (store.fuelOilBunkerTable().bunkerRows || []) as BunkerRowFormState[],
          fuelSpecsVm.getFuelSpecMinMaxValues
        );
      }),
    };
  })
);
